import { CommonModule } from '@angular/common'
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { AuthService } from '@auth/services/auth.service'
import { InvitationJob } from '@database/models/invitation-job.interface'
import { NotificationChannel, notificationChannelDisplayName } from '@database/models/resend-confirmation-pricing'
import { InvitationsService } from '@database/services/invitations.service'
import { ServerRepresentedEvent } from '@events/models/event.model'
import { NbButtonModule, NbCardModule, NbDialogRef, NbIconModule } from '@nebular/theme'
import { forkJoin, interval, of, Subscription } from 'rxjs'
import { map, startWith, switchMap } from 'rxjs/operators'

const HISTORY_POLL_MS = 5000

@Component({
  selector: 'app-invitation-jobs-history',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatProgressBarModule, NbCardModule, NbButtonModule, NbIconModule],
  templateUrl: './invitation-jobs-history.component.html',
  styleUrls: ['./invitation-jobs-history.component.scss']
})
export class InvitationJobsHistoryComponent implements OnInit {
  @Input() currentEvent!: ServerRepresentedEvent

  /** Same as invite modal — function ref so template can call channelLabel(send_method). */
  readonly channelLabel = notificationChannelDisplayName

  jobs: InvitationJob[] = []
  loading = false
  error: string | null = null

  private pollSub?: Subscription
  private readonly _destroyRef = inject(DestroyRef)

  constructor(
    private readonly invitationsService: InvitationsService,
    private readonly auth: AuthService,
    protected readonly dialogRef: NbDialogRef<InvitationJobsHistoryComponent>
  ) {}

  ngOnInit(): void {
    this.startPolling()
  }

  close(): void {
    this.dialogRef.close()
  }

  loadJobs(): void {
    this.fetchJobs()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (jobs) => (this.jobs = jobs),
        error: () => {
          this.jobs = []
          this.loading = false
        }
      })
  }

  /** Inbound = invites into the open event. */
  isInbound(job: InvitationJob): boolean {
    return job.to_event?.id === this.currentEvent?.id
  }

  peerEventName(job: InvitationJob): string {
    return this.isInbound(job) ? job.from_event.name : job.to_event.name
  }

  /** Inbound: into this event (down-left). Outbound: out of this event (up-right). */
  directionIcon(job: InvitationJob): string {
    return this.isInbound(job) ? 'south_west' : 'north_east'
  }

  jobSendMethod(job: InvitationJob): NotificationChannel {
    const method = job.send_method ?? NotificationChannel.EMAIL
    return method as NotificationChannel
  }

  private startPolling(): void {
    this.pollSub = interval(HISTORY_POLL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.fetchJobs()),
        map((jobs) => {
          this.jobs = jobs
          return jobs
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (jobs) => {
          if (!this.hasActiveJobs(jobs)) {
            this.stopPolling()
          }
        },
        error: () => {
          this.jobs = []
          this.loading = false
        }
      })
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe()
    this.pollSub = undefined
  }

  private fetchJobs() {
    if (!this.currentEvent?.id) {
      this.loading = false
      return of([] as InvitationJob[])
    }
    if (this.jobs.length === 0) {
      this.loading = true
    }
    this.error = null

    const slug = this.auth.currentWorkspace.slug
    const eventId = this.currentEvent.id

    return forkJoin({
      inbound: this.invitationsService.getInvitationJobs(slug, { to_event_id: eventId }),
      outbound: this.invitationsService.getInvitationJobs(slug, { from_event_id: eventId })
    }).pipe(
      map(({ inbound, outbound }) => {
        this.loading = false
        const merged = [...(inbound.data ?? []), ...(outbound.data ?? [])]
        return this.sortJobs(merged)
      })
    )
  }

  /** Inbound first, then outbound; newest first within each group. */
  private sortJobs(jobs: InvitationJob[]): InvitationJob[] {
    return [...jobs].sort((a, b) => {
      const dir = (this.isInbound(a) ? 0 : 1) - (this.isInbound(b) ? 0 : 1)
      if (dir !== 0) {
        return dir
      }
      return (b.started_at ?? '').localeCompare(a.started_at ?? '')
    })
  }

  private hasActiveJobs(jobs: InvitationJob[]): boolean {
    return jobs.some((j) => j.status === 'queued' || j.status === 'processing')
  }

  effectiveTotal(job: InvitationJob): number {
    const requested = job.requested_count || 0
    if (requested > 0) {
      return requested
    }
    return Math.max(job.invite_count || 0, job.delivered_count || 0, 0)
  }

  progressCaption(job: InvitationJob): string {
    if (job.status === 'queued' || job.status === 'processing') {
      const total = this.effectiveTotal(job)
      const ingested = job.invite_count || 0
      if (total > 0 && ingested < total) {
        return 'Creating invitations'
      }
      return 'Sending invitations'
    }
    if (job.status === 'partial') {
      return 'Delivery (partial)'
    }
    if (job.status === 'failed') {
      return 'Failed'
    }
    return 'Delivery'
  }

  progressPercent(job: InvitationJob): number {
    const total = this.effectiveTotal(job)
    if (total <= 0) {
      return job.status === 'completed' || job.status === 'partial' ? 100 : 0
    }
    const ingested = job.invite_count || 0
    if (job.status === 'queued' || job.status === 'processing') {
      if (ingested >= total) {
        const delivered = job.delivered_count || 0
        return Math.min(100, Math.round((delivered / total) * 100))
      }
      return Math.min(100, Math.round((ingested / total) * 100))
    }
    const delivered = job.delivered_count || 0
    return Math.min(100, Math.round((delivered / total) * 100))
  }

  statusLabel(job: InvitationJob): string {
    switch (job.status) {
      case 'completed':
        return 'Completed'
      case 'processing':
        return 'In progress'
      case 'queued':
        return 'Queued'
      case 'partial':
        return 'Partial'
      case 'failed':
        return 'Failed'
      default:
        return job.status
    }
  }

  statusClass(job: InvitationJob): string {
    switch (job.status) {
      case 'completed':
        return 'tw-bg-emerald-100 tw-text-emerald-800'
      case 'processing':
        return 'tw-bg-amber-100 tw-text-amber-900'
      case 'partial':
        return 'tw-bg-orange-100 tw-text-orange-900'
      case 'failed':
        return 'tw-bg-red-100 tw-text-red-800'
      default:
        return 'tw-bg-slate-100 tw-text-slate-700'
    }
  }
}
